import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import heroBg from "@/assets/hero-bg.jpg";

const Hero = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);
  // Normalized pointer offset from section center, range -1..1
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const el = sectionRef.current;
    if (!el) return;

    let ticking = false;
    let nextX = 0;
    let nextY = 0;

    const apply = () => {
      setPointer({ x: nextX, y: nextY });
      ticking = false;
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const halfW = rect.width / 2;
      const halfH = rect.height / 2;
      const nx = (e.clientX - rect.left - halfW) / halfW;
      const ny = (e.clientY - rect.top - halfH) / halfH;
      nextX = Math.min(1, Math.max(-1, nx));
      nextY = Math.min(1, Math.max(-1, ny));
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    const onLeave = () => {
      nextX = 0;
      nextY = 0;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    };

    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerleave", onLeave);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  const { x: nx, y: ny } = pointer;
  const blobTransition =
    "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), filter 500ms ease-out";

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[78vh] md:min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <img
        src={heroBg}
        alt={t("hero.bgAlt")}
        className="absolute inset-0 w-full h-full object-cover opacity-40"
        loading="eager"
        width="1920"
        height="1080"
      />

      {/* Mesh gradient layer */}
      <div className="absolute inset-0 bg-gradient-mesh" />

      {/* Floating orbs — driven by mouse movement */}
      <div
        aria-hidden
        className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-primary/25 blur-3xl will-change-transform"
        style={{
          transform: `translate3d(${nx * 70}px, ${ny * 60}px, 0) scale(${1 + Math.abs(nx) * 0.08})`,
          filter: `blur(48px) hue-rotate(${nx * 12}deg)`,
          transition: blobTransition,
        }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 -right-32 w-[32rem] h-[32rem] rounded-full blur-3xl will-change-transform"
        style={{
          backgroundColor: "hsl(var(--accent-violet) / 0.32)",
          transform: `translate3d(${nx * -90}px, ${ny * -75}px, 0) scale(${1 + Math.abs(ny) * 0.1})`,
          filter: `blur(48px) hue-rotate(${nx * -16}deg)`,
          transition: blobTransition,
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-primary/15 blur-3xl will-change-transform"
        style={{
          transform: `translate3d(${nx * 50}px, ${ny * -55}px, 0) scale(${1 + Math.abs(nx) * 0.07})`,
          filter: `blur(48px) hue-rotate(${ny * 10}deg)`,
          transition: blobTransition,
        }}
      />

      {/* Subtle dot grid for texture */}
      <div className="absolute inset-0 bg-grid-faint opacity-50" />

      {/* Bottom fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center">
        <h1
          className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight animate-fade-in-up"
          style={{ animationDelay: "120ms" }}
        >
          {t("hero.titlePre")}{" "}
          <span className="text-gradient-brand">{t("hero.titleAccent")}</span>
        </h1>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-10" aria-hidden="true">
        <div className="w-0 h-0 animate-scroll-nudge"
          style={{
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderTop: '12px solid hsl(var(--muted-foreground) / 0.6)',
          }}
        />
      </div>

    </section>
  );
};

export default Hero;
